import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthStore } from "../../stores/auth-store";
import { fallbackKpiData, kpiData } from "../../mock-data/kpiData";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyOrderRepositoryImpl } from "../../../infrastructure/repositories/company/company-order-repository-impl";
import { CompanyOrderUsecase } from "../../../domain/usecases/company/company-order-usecase";
import type { Order } from "./OrderManagement";
import type {
  Courier,
  OrderOptionResponse,
} from "../../../domain/types/orders";
import type { MachineDetail } from "../../../domain/types/machine-detail";
import { companyMachineUseCase } from "../../../di/container";
import { mapApiOrderToView } from "./OrderManagement/utils";
import {
  DndContext,
  type DragEndEvent, DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";

// 칸반 상태 타입 (6개 컬럼)
type KanbanStatus =
  | "accepted"
  | "resubmission"
  | "production"
  | "production_complete"
  | "shipping"
  | "delivered";

const KANBAN_STATUS_LABELS: Record<KanbanStatus, string> = {
  accepted: "접수",
  resubmission: "재접수",
  production: "제작",
  production_complete: "제작 완료",
  shipping: "출고/배송",
  delivered: "배송 완료",
};

// 컬럼별 배경색
const getColumnBgColor = (columnId: KanbanStatus): string => {
  switch (columnId) {
    case "accepted":
    case "resubmission":
      return "#F2F2F2";
    case "production":
    case "production_complete":
      return "#F5F8FF";
    case "shipping":
    case "delivered":
      return "#E8EFFF";
    default:
      return "#F2F2F2";
  }
};

// 카드 타이틀 색상
const getTitleColor = (status: KanbanStatus): string => {
  switch (status) {
    case "production":
    case "production_complete":
    case "shipping":
    case "delivered":
      return "#4285F4";
    default:
      return "#111827";
  }
};

interface KanbanCard {
  id: string;
  title: string;
  status: KanbanStatus;
  orderNumber: string;
  customerName: string;
  orderDate: string;
  quantity: number;
  options?: OrderOptionResponse[];
}

interface KanbanColumn {
  id: KanbanStatus;
  title: string;
  count: number;
  cards: KanbanCard[];
}

// Order를 KanbanCard로 변환
const orderToKanbanCard = (order: Order): KanbanCard | null => {
  const validStatuses: KanbanStatus[] = [
    "accepted",
    "resubmission",
    "production",
    "production_complete",
    "shipping",
    "delivered",
  ];

  if (!validStatuses.includes(order.status as KanbanStatus)) {
    return null;
  }

  return {
    id: order.id,
    title: order.productName,
    status: order.status as KanbanStatus,
    orderNumber: order.orderNumber,
    customerName: order.customerName,
    orderDate: order.orderDate,
    quantity: order.quantity,
    options: order.options,
  };
};

// 드롭 가능한 컬럼 컴포넌트
const DroppableColumn: React.FC<{
  column: KanbanColumn;
  onCardClick: (cardId: string) => void;
  collapsedCards: Set<string>;
  onToggleCard: (cardId: string) => void;
}> = ({ column, onCardClick, collapsedCards, onToggleCard }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const bgColor = getColumnBgColor(column.id);

  return (
    <div className="flex flex-col p-[1.5px] min-w-[240px] w-[240px] flex-shrink-0">
      <div className="text-center mb-[0.25rem]">
        <h3 className="text-sm text-start ml-2 font-medium text-gray-700 mb-[0.25rem]">
          {column.title} ({column.count})
        </h3>
      </div>

      <div
        ref={setNodeRef}
        className={`overflow-hidden min-h-[37.5rem] h-full rounded-[0.5rem] p-[0.5rem] transition-colors ${
          isOver ? "bg-blue-50 ring-2 ring-blue-300" : ""
        }`}
        style={{ backgroundColor: isOver ? undefined : bgColor }}
      >
        <div className="relative">
          <SortableContext
            items={column.cards.map((card) => card.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3 min-h-[100px]">
              {column.cards.map((card) => (
                <DraggableCard
                  key={card.id}
                  card={card}
                  onClick={() => onCardClick(card.id)}
                  isExpanded={!collapsedCards.has(card.id)}
                  onToggle={() => onToggleCard(card.id)}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

// 옵션을 그룹별로 정리하는 헬퍼 함수
const groupOptionsByCategory = (options: OrderOptionResponse[]) => {
  const groups: Record<string, { name: string; value: string }[]> = {};

  options.forEach((opt) => {
    // optionName이 "그룹/속성" 형식인지 확인 (예: "용지/크기", "인쇄방식/코팅")
    const parts = opt.optionName.split('/');
    if (parts.length >= 2) {
      const groupName = parts[0].trim();
      const attrName = parts.slice(1).join('/').trim();
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push({ name: attrName, value: opt.optionValue });
    } else {
      // 그룹이 없는 경우 "옵션"이라는 기본 그룹에 추가
      if (!groups["옵션"]) {
        groups["옵션"] = [];
      }
      groups["옵션"].push({ name: opt.optionName, value: opt.optionValue });
    }
  });

  return groups;
};

// 드래그 가능한 카드 컴포넌트
const DraggableCard: React.FC<{
  card: KanbanCard;
  onClick: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ card, onClick, isExpanded, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // 드래그 중이 아닐 때만 클릭 이벤트 발생
    if (!isDragging) {
      onClick();
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const optionGroups = card.options ? groupOptionsByCategory(card.options) : {};
  const groupEntries = Object.entries(optionGroups);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className="bg-white rounded-[4px] p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow w-full"
    >
      {/* 제품명 */}
      <div className="flex items-center justify-between mb-2">
        <h4
          className="text-[16px] font-semibold truncate text-left flex-1"
          style={{ color: "#555555" }}
        >
          {card.title}
        </h4>
        <button
          onClick={handleToggle}
          className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label={isExpanded ? "접기" : "펼치기"}
        >
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* 옵션 영역 */}
      <div
        className={`text-[13px] overflow-hidden transition-all duration-200 ${
          isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {/* 옵션 그룹들 */}
        {groupEntries.length > 0 && groupEntries.map(([groupName, attrs], groupIdx) => (
          <div
            key={groupName}
            className={`flex gap-[14px] ${groupIdx < groupEntries.length - 1 ? 'border-b border-[#aeaeae] pb-[10px] mb-[10px]' : ''}`}
          >
            {/* 그룹명 (옵션 카테고리) */}
            <div className="w-[51px] flex-shrink-0">
              <span className="font-bold text-black">{groupName}</span>
            </div>
            {/* 속성-값 목록 */}
            <div className="flex-1 space-y-[2px]">
              {attrs.map((attr, attrIdx) => (
                <div key={attrIdx} className="flex justify-between">
                  <span className="text-black">{attr.name}</span>
                  <span className="text-black">{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 기본 정보 (옵션이 없거나 추가 정보) */}
        {groupEntries.length === 0 && card.options && card.options.length > 0 && (
          <div className="space-y-[2px]">
            {card.options.map((opt, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-black">{opt.optionName}</span>
                <span className="text-black">{opt.optionValue}</span>
              </div>
            ))}
          </div>
        )}

        {/* 수량 */}
        <div className={`flex justify-between ${groupEntries.length > 0 || (card.options && card.options.length > 0) ? 'mt-[10px] pt-[10px] border-t border-[#aeaeae]' : ''}`}>
          <span className="text-black">수량</span>
          <span className="text-black">{card.quantity}</span>
        </div>

        {/* 주문 정보 */}
        <div className="mt-[10px] pt-[10px] border-t border-[#aeaeae] space-y-[2px]">
          <div className="flex justify-between">
            <span className="text-gray-500">주문번호</span>
            <span className="font-medium text-black">{card.orderNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">주문자</span>
            <span className="text-black">{card.customerName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">주문일</span>
            <span className="text-black">{card.orderDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// 카드 오버레이 (드래그 중 표시)
const CardOverlay: React.FC<{ card: KanbanCard }> = ({ card }) => {
  const optionGroups = card.options ? groupOptionsByCategory(card.options) : {};
  const groupEntries = Object.entries(optionGroups);

  return (
    <div
      className="bg-white rounded-[4px] p-3 shadow-lg border-2 border-blue-300 opacity-90"
      style={{ minWidth: "220px", minHeight: "100px" }}
    >
      {/* 제품명 */}
      <h4
        className="text-[16px] font-semibold mb-2 truncate text-left"
        style={{ color: "#555555" }}
      >
        {card.title}
      </h4>

      <div className="text-[13px]">
        {/* 옵션 그룹들 */}
        {groupEntries.length > 0 && groupEntries.map(([groupName, attrs], groupIdx) => (
          <div
            key={groupName}
            className={`flex gap-[14px] ${groupIdx < groupEntries.length - 1 ? 'border-b border-[#aeaeae] pb-[10px] mb-[10px]' : ''}`}
          >
            {/* 그룹명 */}
            <div className="w-[51px] flex-shrink-0">
              <span className="font-bold text-black">{groupName}</span>
            </div>
            {/* 속성-값 목록 */}
            <div className="flex-1 space-y-[2px]">
              {attrs.map((attr, attrIdx) => (
                <div key={attrIdx} className="flex justify-between">
                  <span className="text-black">{attr.name}</span>
                  <span className="text-black">{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 기본 옵션 표시 (그룹이 없는 경우) */}
        {groupEntries.length === 0 && card.options && card.options.length > 0 && (
          <div className="space-y-[2px]">
            {card.options.map((opt, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-black">{opt.optionName}</span>
                <span className="text-black">{opt.optionValue}</span>
              </div>
            ))}
          </div>
        )}

        {/* 수량 */}
        <div className={`flex justify-between ${groupEntries.length > 0 || (card.options && card.options.length > 0) ? 'mt-[10px] pt-[10px] border-t border-[#aeaeae]' : ''}`}>
          <span className="text-black">수량</span>
          <span className="text-black">{card.quantity}</span>
        </div>

        {/* 주문 정보 */}
        <div className="mt-[10px] pt-[10px] border-t border-[#aeaeae] space-y-[2px]">
          <div className="flex justify-between">
            <span className="text-gray-500">주문번호</span>
            <span className="font-medium text-black">{card.orderNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">주문자</span>
            <span className="text-black">{card.customerName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">주문일</span>
            <span className="text-black">{card.orderDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

type ActiveTabKorean =
  | "장비가동량"
  | "결제건수"
  | "결제금액"
  | "접속자수"
  | "가입자수"
  | "주문량";
type ActiveTabEnglish =
  | "equipmentOperationCount"
  | "paymentCount"
  | "paymentAmount"
  | "visitorCount"
  | "customerCount"
  | "orderCount";

const StatusManagement = () => {
  const [activeTab, setActiveTab] = useState<ActiveTabKorean>("접속자수");
  const [activeId, setActiveId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { companyId } = useParams();

  // API 연동
  const repo = useMemo(() => new CompanyOrderRepositoryImpl(), []);
  const usecase = useMemo(() => new CompanyOrderUsecase(repo), [repo]);

  // 주문 데이터
  const [orders, setOrders] = useState<Order[]>([]);
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[]>([
    { id: "accepted", title: "접수", count: 0, cards: [] },
    { id: "resubmission", title: "재접수", count: 0, cards: [] },
    { id: "production", title: "제작", count: 0, cards: [] },
    { id: "production_complete", title: "제작 완료", count: 0, cards: [] },
    { id: "shipping", title: "출고/배송", count: 0, cards: [] },
    { id: "delivered", title: "배송 완료", count: 0, cards: [] },
  ]);

  // 다이얼로그 상태
  const [isResubmissionOpen, setIsResubmissionOpen] = useState(false);
  const [resubmissionMessage, setResubmissionMessage] = useState("");
  const [isShippingDialogOpen, setIsShippingDialogOpen] = useState(false);
  const [shippingCourier, setShippingCourier] = useState("");
  const [shippingTrackingNumber, setShippingTrackingNumber] = useState("");
  const [courierList, setCourierList] = useState<Courier[]>([]);
  const [isCourierLoading, setIsCourierLoading] = useState(false);

  // 드래그 중인 카드 및 대상 정보
  const [pendingMove, setPendingMove] = useState<{
    cardId: string;
    targetColumn: KanbanStatus;
  } | null>(null);

  // 에러 모달
  const [errorMessageTitle, setErrorMessageTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 칸반 스크롤 관련
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 카드 접힘 상태 관리 (기본은 열림)
  const COLLAPSED_CARDS_KEY = `kanban_collapsed_cards_${companyId}`;

  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem(COLLAPSED_CARDS_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  const handleToggleCard = useCallback(
    (cardId: string) => {
      setCollapsedCards((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(cardId)) {
          newSet.delete(cardId);
        } else {
          newSet.add(cardId);
        }
        sessionStorage.setItem(
          COLLAPSED_CARDS_KEY,
          JSON.stringify(Array.from(newSet)),
        );
        return newSet;
      });
    },
    [COLLAPSED_CARDS_KEY],
  );

  const COLUMN_WIDTH = 250; // 240px + 10px gap

  // 스크롤 가능 여부 체크
  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // 스크롤 이동 핸들러
  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  // 스크롤 이벤트 리스너 및 초기 체크
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollability();
    container.addEventListener("scroll", checkScrollability);
    window.addEventListener("resize", checkScrollability);

    return () => {
      container.removeEventListener("scroll", checkScrollability);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability, kanbanColumns]);

  const activeTabNames: Record<ActiveTabKorean, ActiveTabEnglish> = {
    장비가동량: "equipmentOperationCount",
    결제건수: "paymentCount",
    결제금액: "paymentAmount",
    접속자수: "visitorCount",
    가입자수: "customerCount",
    주문량: "orderCount",
  };

  interface ChartData {
    date: string;
    equipmentOperationCount: number;
    paymentAmount: number;
    paymentCount: number;
    visitorCount: number;
    customerCount: number;
    orderCount: number;
  }
  const [chartDataArray, setChartDataArray] = useState<ChartData[]>([]);
  const [companyMachines, setCompanyMachines] = useState<MachineDetail[]>([]);

  // 차트 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        // const a = await companyUseCase.getCompanyRealtimeGraph(
        //   Number(companyId),
        // );

        interface OnePieceOfData {
          date: string;
          paymentAmount: number;
          visitorCount: number;
          customerCount: number;
          orderCount: number;
          paymentCount: number; // put 0
          equipmentOperationCount: number; // put 0
          dailyProduction: number; // put 0
          dailyLeadTime: number; // put 0
          operationRate: number; // put 0
          deliveryComplianceRate: number; // put 0
        }

        const a: OnePieceOfData[] = [
          {
            date: "2026-02-20",
            paymentAmount: 120000,
            visitorCount: 32,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-19",
            paymentAmount: 85000,
            visitorCount: 21,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-18",
            paymentAmount: 94000,
            visitorCount: 18,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-17",
            paymentAmount: 150000,
            visitorCount: 40,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-16",
            paymentAmount: 73000,
            visitorCount: 25,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-15",
            paymentAmount: 200000,
            visitorCount: 45,
            customerCount: 2,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-14",
            paymentAmount: 50000,
            visitorCount: 12,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-13",
            paymentAmount: 110000,
            visitorCount: 30,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-12",
            paymentAmount: 68000,
            visitorCount: 20,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-11",
            paymentAmount: 175000,
            visitorCount: 41,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-10",
            paymentAmount: 92000,
            visitorCount: 27,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-09",
            paymentAmount: 45000,
            visitorCount: 15,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-08",
            paymentAmount: 130000,
            visitorCount: 33,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-07",
            paymentAmount: 39000,
            visitorCount: 10,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-06",
            paymentAmount: 210000,
            visitorCount: 48,
            customerCount: 2,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-05",
            paymentAmount: 60000,
            visitorCount: 19,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-04",
            paymentAmount: 95000,
            visitorCount: 26,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-03",
            paymentAmount: 180000,
            visitorCount: 44,
            customerCount: 2,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-02",
            paymentAmount: 72000,
            visitorCount: 17,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-02-01",
            paymentAmount: 160000,
            visitorCount: 38,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-31",
            paymentAmount: 54000,
            visitorCount: 14,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-30",
            paymentAmount: 140000,
            visitorCount: 35,
            customerCount: 1,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-29",
            paymentAmount: 48000,
            visitorCount: 12,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-28",
            paymentAmount: 175000,
            visitorCount: 42,
            customerCount: 1,
            orderCount: 3,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-27",
            paymentAmount: 35000,
            visitorCount: 9,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-26",
            paymentAmount: 115000,
            visitorCount: 29,
            customerCount: 0,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-25",
            paymentAmount: 88000,
            visitorCount: 22,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-24",
            paymentAmount: 220000,
            visitorCount: 49,
            customerCount: 1,
            orderCount: 4,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-23",
            paymentAmount: 90000,
            visitorCount: 23,
            customerCount: 0,
            orderCount: 1,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
          {
            date: "2026-01-22",
            paymentAmount: 130000,
            visitorCount: 31,
            customerCount: 0,
            orderCount: 2,
            paymentCount: 0,
            equipmentOperationCount: 0,
            dailyProduction: 0,
            dailyLeadTime: 0,
            operationRate: 0,
            deliveryComplianceRate: 0,
          },
        ];

        const formatDateToDH = (dateString: string): string => {
          const date = new Date(dateString);
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${month}.${day}`;
        };

        let sortedData = [...a].sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });

        sortedData = sortedData.reverse();
        sortedData = sortedData.slice(1, 16);
        sortedData = sortedData.reverse();

        sortedData = sortedData.map((item) => ({
          ...item,
          date: formatDateToDH(item.date),
        }));

        sortedData = sortedData.map((item) => {
          return { ...item, equipmentOperationCount: 0 };
        });

        sortedData = sortedData.map((item) => {
          for (let i = 0; i < selectedKpiData.graphData.length; i++) {
            const replacedDate = selectedKpiData.graphData[i].date.replace(
              "/",
              ".",
            );
            if (item.date === replacedDate) {
              if (selectedKpiData.dashBoardGraph === "PRODUCTION") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].dailyProduction,
                };
              } else if (selectedKpiData.dashBoardGraph === "LEAD_TIME") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].dailyLeadTime,
                };
              } else if (selectedKpiData.dashBoardGraph === "OPERATION_RATE") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].operationRate,
                };
              }
              if (selectedKpiData.dashBoardGraph === "DELIVERY_RATE") {
                return {
                  ...item,
                  equipmentOperationCount:
                    selectedKpiData.graphData[i].deliveryComplianceRate,
                };
              }
            }
          }
          return item;
        });

        setChartDataArray(sortedData);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId]);

  // 장비 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        const machineDetails = await companyMachineUseCase.getMachineDetails(
          Number(companyId),
        );
        setCompanyMachines(machineDetails ?? []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId]);

  // 주문 데이터 로드 및 칸반 동기화
  const loadOrders = async () => {
    try {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      const result = await usecase.getOrders(cid);
      const mapped = result.items.map(mapApiOrderToView);
      setOrders(mapped);
      syncKanbanWithOrders(mapped);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("주문 불러오기 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    loadOrders();
  }, [companyId]);

  // 최근 일주일 이내의 주문인지 확인
  const isWithinOneWeek = (dateString: string): boolean => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return orderDate >= oneWeekAgo;
  };

  // 칸반 동기화 (최근 일주일 데이터만 표시)
  const syncKanbanWithOrders = (orderList: Order[]) => {
    const columnIds: KanbanStatus[] = [
      "accepted",
      "resubmission",
      "production",
      "production_complete",
      "shipping",
      "delivered",
    ];

    const updatedColumns = columnIds.map((columnId) => {
      // 배송 완료 컬럼만 최근 일주일 데이터 필터링 적용
      const relevantOrders = orderList.filter((order) => {
        if (order.status !== columnId) return false;
        if (columnId === "delivered") {
          return isWithinOneWeek(order.orderDate);
        }
        return true;
      });
      const cards = relevantOrders
        .map(orderToKanbanCard)
        .filter((card): card is KanbanCard => card !== null);

      return {
        id: columnId,
        title: KANBAN_STATUS_LABELS[columnId],
        count: cards.length,
        cards,
      };
    });

    setKanbanColumns(updatedColumns);

    // 배송 완료 카드들은 기본적으로 접힌 상태로 설정 (세션에 저장된 값이 없는 경우에만)
    const savedCollapsed = sessionStorage.getItem(COLLAPSED_CARDS_KEY);
    if (!savedCollapsed) {
      const deliveredColumn = updatedColumns.find(
        (col) => col.id === "delivered",
      );
      if (deliveredColumn && deliveredColumn.cards.length > 0) {
        const deliveredCardIds = deliveredColumn.cards.map((card) => card.id);
        setCollapsedCards(new Set(deliveredCardIds));
        sessionStorage.setItem(
          COLLAPSED_CARDS_KEY,
          JSON.stringify(deliveredCardIds),
        );
      }
    }
  };

  // 카드 클릭 - 상세 페이지로 이동
  const handleCardClick = (cardId: string) => {
    navigate(`/company/${companyId}/orders?mode=detail&orderId=${cardId}`);
  };

  // 드래그 관련
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeCardId = active.id as string;
    let overColumnId = over.id as string;

    // over가 카드인 경우, 해당 카드의 컬럼 찾기
    const sourceColumn = kanbanColumns.find((col) =>
      col.cards.some((card) => card.id === activeCardId),
    );

    // 유효한 컬럼인지 확인
    const validColumnIds: KanbanStatus[] = [
      "accepted",
      "resubmission",
      "production",
      "production_complete",
      "shipping",
      "delivered",
    ];

    // over가 카드 ID인 경우 해당 카드가 속한 컬럼 찾기
    if (!validColumnIds.includes(overColumnId as KanbanStatus)) {
      const targetColumn = kanbanColumns.find((col) =>
        col.cards.some((card) => card.id === overColumnId),
      );
      if (targetColumn) {
        overColumnId = targetColumn.id;
      } else {
        return;
      }
    }

    // 같은 컬럼이면 리턴
    if (sourceColumn?.id === overColumnId) return;

    const targetStatus = overColumnId as KanbanStatus;

    // 재접수인 경우 다이얼로그 표시
    if (targetStatus === "resubmission") {
      setPendingMove({ cardId: activeCardId, targetColumn: targetStatus });
      setResubmissionMessage("");
      setIsResubmissionOpen(true);
      return;
    }

    // 출고/배송인 경우 다이얼로그 표시
    if (targetStatus === "shipping") {
      setPendingMove({ cardId: activeCardId, targetColumn: targetStatus });
      setShippingCourier("");
      setShippingTrackingNumber("");
      setIsShippingDialogOpen(true);

      // 택배사 목록 로드
      if (courierList.length === 0) {
        setIsCourierLoading(true);
        try {
          const couriers = await usecase.getCourierList();
          setCourierList(couriers);
        } catch (e: any) {
          console.error(e);
        } finally {
          setIsCourierLoading(false);
        }
      }
      return;
    }

    // 그 외 상태 변경은 바로 API 호출
    await executeStatusChange(activeCardId, targetStatus);
  };

  // 상태 변경 실행
  const executeStatusChange = async (
    cardId: string,
    targetStatus: KanbanStatus,
  ) => {
    try {
      const cid = Number(companyId);
      const oid = Number(cardId);

      switch (targetStatus) {
        case "accepted":
          await usecase.acceptOrder(cid, oid);
          break;
        case "production":
          await usecase.startProduction(cid, oid);
          break;
        case "production_complete":
          await usecase.completeProduction(cid, oid);
          break;
        case "delivered":
          await usecase.completeDelivery(cid, oid);
          break;
        default:
          break;
      }

      await loadOrders();
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("상태 변경 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 재접수 확인
  const handleConfirmResubmission = async () => {
    if (!resubmissionMessage.trim()) {
      alert("수정 요청 사항을 입력해주세요");
      return;
    }

    if (!pendingMove) return;

    try {
      const cid = Number(companyId);
      const oid = Number(pendingMove.cardId);

      await usecase.resubmission(cid, oid, resubmissionMessage);
      await loadOrders();

      setIsResubmissionOpen(false);
      setResubmissionMessage("");
      setPendingMove(null);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("재접수 요청 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 출고/배송 확인
  const handleConfirmShipping = async () => {
    if (!pendingMove) return;

    try {
      const cid = Number(companyId);
      const oid = Number(pendingMove.cardId);

      await usecase.shipOrder(
        cid,
        oid,
        shippingCourier || undefined,
        shippingTrackingNumber || undefined,
      );
      await loadOrders();

      setIsShippingDialogOpen(false);
      setShippingCourier("");
      setShippingTrackingNumber("");
      setPendingMove(null);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("출고/배송 처리 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 다이얼로그 닫기
  const handleCloseResubmission = () => {
    setIsResubmissionOpen(false);
    setResubmissionMessage("");
    setPendingMove(null);
  };

  const handleCloseShipping = () => {
    setIsShippingDialogOpen(false);
    setShippingCourier("");
    setShippingTrackingNumber("");
    setPendingMove(null);
  };

  // 드래그 중인 카드 찾기
  const draggedCard = activeId
    ? kanbanColumns
        .flatMap((col) => col.cards)
        .find((card) => card.id === activeId)
    : null;
  const { user } = useAuthStore();

  const selectedKpiData =
    kpiData.filter((data) => data.companyId === Number(user?.companyId))[0] ??
    fallbackKpiData;
  const metrics = selectedKpiData.metrics;

  const [usingName, setUsingName] = useState("");

  useEffect(() => {
    if (selectedKpiData.dashBoardGraph === "PRODUCTION") {
      setUsingName("생산량");
    } else if (selectedKpiData.dashBoardGraph === "LEAD_TIME") {
      setUsingName("수주출하 리드타임");
    } else if (selectedKpiData.dashBoardGraph === "OPERATION_RATE") {
      setUsingName("가동량");
    } else if (selectedKpiData.dashBoardGraph === "DELIVERY_RATE") {
      setUsingName("납기 준수율");
    } else if (selectedKpiData.dashBoardGraph === "VISITOR_COUNT") {
      setUsingName("접속자수");
    } else if (selectedKpiData.dashBoardGraph === "CUSTOMER_COUNT") {
      setUsingName("가입자수");
    } else if (selectedKpiData.dashBoardGraph === "ORDER_COUNT") {
      setUsingName("주문량");
    } else if (selectedKpiData.dashBoardGraph === "PAYMENT_AMOUNT") {
      setUsingName("결제금액");
    }
  }, []);

  return (
    <>
      <CompanyAdminLayout>
        <>
          <div className="p-5 relative">
            <div className="relative mb-[28px] / flex flex-col items-start gap-[0.5rem]">
              <h2 className="text-[1.375rem] / font-semibold">현황 관리</h2>
              <span className="text-[1rem] font-normal">
                주문 현황을 칸반으로 관리해 보세요.
              </span>
            </div>
            <div className="bg-white rounded-[0.625rem] shadow-sm p-[18px]">
              <h2 className="text-lg font-semibold text-gray-900 mb-[0.75rem] text-left p-[2px]">
                작업 현황
              </h2>

              <div className="relative">
                <style>{`
            .kanban-scroll-container {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .kanban-scroll-container::-webkit-scrollbar {
              display: none;
              width: 0;
              height: 0;
            }
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes slideOutLeft {
              from {
                opacity: 1;
                transform: translateX(0);
              }
              to {
                opacity: 0;
                transform: translateX(-20px);
              }
            }
            @keyframes slideInRight {
              from {
                opacity: 0;
                transform: translateX(20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes slideOutRight {
              from {
                opacity: 1;
                transform: translateX(0);
              }
              to {
                opacity: 0;
                transform: translateX(20px);
              }
            }
            .scroll-btn-left {
              animation: slideInLeft 0.3s ease-out forwards;
            }
            .scroll-btn-left-hide {
              animation: slideOutLeft 0.3s ease-out forwards;
              pointer-events: none;
            }
            .scroll-btn-right {
              animation: slideInRight 0.3s ease-out forwards;
            }
            .scroll-btn-right-hide {
              animation: slideOutRight 0.3s ease-out forwards;
              pointer-events: none;
            }
          `}</style>

                {/* 왼쪽 스크롤 버튼 */}
                <button
                  onClick={handleScrollLeft}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-r-full bg-white/80 shadow-[10px_10px_20px_-3px_rgba(0,0,0,0.1)]
              flex items-center justify-center
              hover:bg-white transition-colors
              ${canScrollLeft ? "scroll-btn-left" : "scroll-btn-left-hide"}`}
                  style={{ display: canScrollLeft ? "flex" : undefined }}
                  aria-label="이전 컬럼으로 이동"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* 오른쪽 스크롤 버튼 */}
                <button
                  onClick={handleScrollRight}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-l-full bg-white/80 shadow-[-10px_10px_20px_-3px_rgba(0,0,0,0.1)]
              flex items-center justify-center
              hover:bg-white transition-colors
              ${canScrollRight ? "scroll-btn-right" : "scroll-btn-right-hide"}`}
                  style={{ display: canScrollRight ? "flex" : undefined }}
                  aria-label="다음 컬럼으로 이동"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                <div
                  ref={scrollContainerRef}
                  className="kanban-scroll-container overflow-x-auto"
                >
                  <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="flex gap-[0.625rem] justify-start min-w-max px-1 py-1">
                      {kanbanColumns.map((column) => (
                        <DroppableColumn
                          key={column.id}
                          column={column}
                          onCardClick={handleCardClick}
                          collapsedCards={collapsedCards}
                          onToggleCard={handleToggleCard}
                        />
                      ))}
                    </div>

                    <DragOverlay>
                      {draggedCard ? <CardOverlay card={draggedCard} /> : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              </div>
            </div>
          </div>
        </>
      </CompanyAdminLayout>
    </>
  );
};

export default StatusManagement;
